import { z } from "zod";

// ============================================================================
// PAGINATION
// ============================================================================

export const PaginationParamsSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});

// ============================================================================
// BOOLEAN QUERY PARAMETERS
// ============================================================================

/**
 * A boolean carried in a query string, where every value arrives as text.
 *
 * **Never `z.coerce.boolean()`.** That is `Boolean(value)`, so the string
 * `"false"` — the single most likely thing a caller sends to mean false —
 * coerces to `true`, along with `"0"`, `"no"` and every other non-empty string.
 * Only the empty string would be false. Same family as `z.coerce.date()`
 * reading `0` as 1970: a coercion that accepts everything reports no error and
 * returns the wrong answer.
 *
 * Accepts the four spellings a client actually sends plus a real boolean, so an
 * in-process caller passing `true` works without stringifying. Anything else is
 * a `ZodError` → 400 through the global `ZodExceptionFilter`, which is the
 * point: `?verbose=yes` should say so rather than quietly mean false.
 *
 * The accept/reject table is pinned in `common.schemas.test.ts`. Read it before
 * widening or narrowing — every entry there is a decision.
 */
export const BooleanQueryParamSchema = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
  .transform((value) => value === true || value === "true" || value === "1");

/**
 * An optional query parameter, where an EMPTY value means absent.
 *
 * Express parses with `qs`, which turns both `?flag` and `?flag=` into the empty
 * string — not `undefined`. So a bare flag and a cleared filter both arrive as
 * `""`, and a plain `.optional()` rejects them: `?deploymentId=` would 400
 * instead of meaning "no filter", and `?verbose` would 400 instead of being the
 * ordinary way to spell a boolean flag.
 *
 * Wrapping instead of loosening each schema keeps the distinction where it
 * belongs: the FIELD still says what a present value must look like, and this
 * says how the transport spells absence. Applied to every optional v1 query
 * parameter added with it, so the three routes cannot disagree about what an
 * empty value means.
 *
 * ── `.optional()` goes on BOTH halves, and each half buys a different thing ──
 *
 * There are three spellings and only one of them is right. Measured with zod
 * 4.1.13, and pinned in `common.schemas.test.ts`:
 *
 *   spelling                                  `?x=`   emitted `required`
 *   ────────────────────────────────────────  ─────   ──────────────────
 *   `z.preprocess(fn, s).optional()`          400 ❌  absent ✅
 *   `z.preprocess(fn, s.optional())`          200 ✅  ["x"]  ❌
 *   `z.preprocess(fn, s.optional()).optional()` 200 ✅ absent ✅
 *
 * INNER, because the outer wrapper alone short-circuits only when the raw input
 * is already `undefined` — an empty string still reaches `schema`, which is not
 * optional, and 400s. That is the runtime bug this helper exists to fix.
 *
 * OUTER, because `z.toJSONSchema(..., { io: "input" })` asks the OUTERMOST node
 * whether the field may be omitted, and a bare `z.preprocess(...)` answers no
 * however optional its inner schema is. The field is then emitted as REQUIRED.
 * Nothing in the HTTP stack reads that schema, so the mistake is invisible over
 * the wire — but `mcp/infrastructure/catalog/build-tool-input-schema.ts` reads
 * it to publish the MCP tool catalog, and pushes `"query"` onto a tool's
 * top-level `required` as soon as the query slot has one required field. An
 * inner-only helper therefore tells every MCP client that a purely optional
 * filter is mandatory: `tools_search` went from zero required inputs to
 * `required: ["query"]` with a mandatory `type` inside it, i.e. "you must pick a
 * tool type to search the marketplace". Pinned in `mcp/__tests__/tool-catalog.spec.ts`.
 */
export const optionalQueryParam = <T extends z.ZodType>(schema: T) =>
  z.preprocess((value) => (value === "" ? undefined : value), schema.optional()).optional();

export const PaginationMetaSchema = z.object({
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int()
});

// ============================================================================
// ERROR FORMAT
// ============================================================================

export const ApiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional()
  })
});

// ============================================================================
// SUCCESS WRAPPER
// ============================================================================

export const ApiSuccessResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
    meta: PaginationMetaSchema.optional()
  });

// ============================================================================
// STORED ASSIGNMENT IDS
// ============================================================================

/**
 * The id of the entity a folder assignment row points at, as it is STORED.
 *
 * Every `*FolderAssignment` table keys the assigned entity through a bare
 * `String` column with no `@db.Uuid` and no foreign key — `AgentFolderAssignment.agentId`,
 * `ToolFolderAssignment.toolId`, `DocumentTemplateFolderAssignment.documentTemplateId`
 * and `DeploymentFolderAssignment.deploymentId` all have this shape, while their
 * `folderId` siblings carry a real relation. So rows written before the request
 * bodies were validated may hold anything.
 *
 * Response schemas therefore describe the column, not the intent: uuid-checking a
 * read turns one legacy row into a failed folder listing instead of a caught
 * mistake. The REQUEST bodies are uuid-checked, which is what stops new bad rows.
 *
 * Use this only for reads of those columns. A request body takes `z.string().uuid()`.
 */
export const StoredAssignmentEntityIdSchema = z.string();

// ============================================================================
// COMMON PARAMS
// ============================================================================

export const UuidParamSchema = z.object({
  id: z.string().uuid()
});

export const AgentIdParamSchema = z.object({
  agentId: z.string().uuid()
});

export const AgentAndToolIdParamSchema = z.object({
  agentId: z.string().uuid(),
  toolId: z.string().uuid()
});

export const AgentAndVersionIdParamSchema = z.object({
  agentId: z.string().uuid(),
  versionId: z.string().uuid()
});

export const FolderIdParamSchema = z.object({
  folderId: z.string().uuid()
});

export const ScenarioIdParamSchema = z.object({
  scenarioId: z.string().uuid()
});

export const ThreadIdParamSchema = z.object({
  threadId: z.string().uuid()
});

export const DeploymentAndSessionIdParamSchema = z.object({
  deploymentId: z.string().uuid(),
  sessionId: z.string().uuid()
});

export const DeploymentAndTemplateIdParamSchema = z.object({
  deploymentId: z.string().uuid(),
  templateId: z.string().min(1)
});

export const GenerateProfilePictureBodySchema = z.object({
  customPrompt: z.string().max(2000).optional()
});

// ============================================================================
// OPEN JSON SHAPES
// ============================================================================

/**
 * A JSON Schema document supplied by a caller — a tool's output contract, a
 * judge's structured-output shape, a skill task's input/output format.
 *
 * These were `z.unknown()`, which is not "any JSON" but *nothing at all*: it
 * converts to `{}`, so an MCP tool catalog, a client generator or a documenting
 * consumer sees a field with no type, no shape and no guidance, and the only
 * hint that an object is expected lived in a `.refine()` message that
 * `z.toJSONSchema` discards.
 *
 * `z.record(z.string(), z.unknown())` is the honest floor: a JSON Schema
 * document is always a JSON object, never an array or a scalar, and the keys
 * inside it are open by design. It converts to a real object schema and it
 * rejects the shapes that could only ever have been a caller mistake.
 *
 * Deliberately NOT a recursive model of the draft 2020-12 meta-schema. The
 * platform validates these documents against the real meta-schema with Ajv at
 * the save boundary (`validate-tool-input-schema.ts`); re-expressing that in Zod
 * would be a second, weaker, drifting copy of a spec — and would reject
 * valid-but-unusual documents this API accepts today.
 */
export const JsonSchemaDocumentSchema = z
  .record(z.string(), z.unknown())
  .describe(
    "A JSON Schema document, e.g. " +
      '{ "type": "object", "properties": { "city": { "type": "string" } }, "required": ["city"] }. ' +
      "Any JSON object is accepted here; the document is validated against JSON Schema " +
      "draft 2020-12 when it is saved."
  );

// ============================================================================
// CUSTOM MODEL SELECTION (BYOM)
// ============================================================================

/**
 * The id of a `CustomModel` row this organization owns, as a v1 WRITE accepts it.
 *
 * One declaration for both surfaces that can select one — the agent's
 * `modelConfig` and the AI task's model block — because two spellings of the
 * same selector is how they drift.
 *
 * 🚨 **`.uuid()` here and NOT on the internal `ModelConfigSchema`, deliberately.**
 * That schema is read at inference (`AITaskExecutor.parseTaskModelConfig`,
 * `resolveCustomModel`), and a stored config that fails to parse is DISCARDED
 * whole — the task then runs a platform model on the platform key with nothing
 * reporting it. Narrowing a read schema converts a legacy value into that
 * silent fallback. Strict on write, tolerant on read: this bounds what a caller
 * may send, and the reader stays `z.string()`.
 *
 * A well-formed id that names no row of this organization is a 404 from the
 * write use case, never a 403 — a 403 would confirm the row exists in some
 * other tenant.
 */
export const CustomModelIdSchema = z
  .string()
  .uuid()
  .describe(
    "Id of a custom model this organization owns, from `GET /custom-models` " +
      '(or the `id` of a `source: "custom"` row in `GET /models`). Selecting a ' +
      "custom model is done by THIS id — never by putting the row's " +
      "`CUSTOM_<PROTOCOL>` provider string in `modelProvider`, which admits only " +
      "the four platform values. `GET /models` also hides a DISABLED custom " +
      "model, while `GET /custom-models` lists it and this field still accepts it."
  );

/**
 * The same id as it is READ BACK — a stored value, not an incoming one.
 *
 * `CustomModel.id` is `@db.Uuid`, so every row really is a uuid and the two
 * schemas agree on today's data. They are still separate on purpose, for the
 * reason `StoredAssignmentEntityIdSchema` above is separate and the reason
 * `id-validation-consistency.test.ts` holds only REQUEST schemas to its
 * cross-file rule: **a response describes rows that already exist.** Tightening
 * one turns a single legacy or hand-written row into a failed listing rather
 * than a caught mistake, and here it would fail it INVISIBLY — the task mapper
 * reads each tuning field through `.catch(undefined)`, so a value the schema
 * refused would be OMITTED from the response, and a task running a customer's
 * endpoint would report running none.
 *
 * Use `CustomModelIdSchema` on anything a caller sends. Use this on anything the
 * database fills in.
 */
export const StoredCustomModelIdSchema = z.string();

// ============================================================================
// AGENT STATUS (Public-facing)
// ============================================================================

export const AgentStatusSchema = z.enum(["ACTIVE", "DRAFT"]);

// ============================================================================
// FOLDER REFERENCE
// ============================================================================

/**
 * Folder a resource belongs to, surfaced on list/get responses (NEX-1918).
 * `null` when the resource is not assigned to any folder.
 */
export const FolderRefSchema = z
  .object({
    id: z.string(),
    name: z.string()
  })
  .nullable();
